import memoize from "lodash/memoize";
import uniq from "lodash/uniq";
import { UserPermissions, hasUserPermission } from "./granularPermissions";
import type { BaseUser } from "@whatslly/server-commons/baseUser";
import { OrgConfigs } from "../../types/src/OrgConfigs";
import { orgConfigsObject } from "@whatslly/commons/OrgConfigsFeatures";
import { canUserLoginToDesktop } from "@whatslly/commons/desktopPermissions";
import { UserType } from "@whatslly/types/User";

export const DOMAIN_TO_INSTANCE_CONFIG = {
  sidecars: "sidecars_enabled",
  destinations: "allow_platform_integrations",
  saml: "show_saml_settings",
  audit_log: "access_audit_log",
  global_layout: "allow_platform_global_layout",
  notifications: "allow_platform_admin_notifications",
  user_management: "allow_inviting_users",
  chat_groups: "show_chat_groups_ui",
  im_groups: "show_im_groups_ui",
  analytics: "show_analytics_ui",
  dashboards: "show_dashboards_ui",
  custom_filters: "show_custom_filters_ui",
  compliance: "analytics_compliance",
  dlp: "allow_dlp",
  incident: "allow_dlp",
  surveillance: "enable_surveillance",
  waba: "allow_waba",
  twilio: "allow_twilio",
  messages_analytics: "allow_messages_analytics",
  settings: "allow_customer_facing_settings",
  api_keys: "allow_public_api",
  url_policy: "allow_url_policy",
  tuvis_contacts: "allow_tuvis_contacts",
  keyword_insights: "allow_keyword_insights",
  phones_whitelist: "allow_phones_whitelist",
  phones_blocklist: "allow_phones_blocklist",
  slack: "allow_slack_platform",
  teams: "allow_teams_platform",
  archive: "allow_archive",
  fraud: "fraud_detection",
  fraud_urls: "phishing_fraud_detection",
  bot_builder: "allow_bot_builder",
  elite_token: "allow_elite_token",
} as const;

export type granularPermissions = keyof typeof UserPermissions;

export const INSTANCE_CONFIG_TO_PERMISSIONS: Partial<Record<granularPermissions, PermissionInstanceConfig>> = {
  "fraud.view_urls": "phishing_fraud_detection",
  "fraud.update_urls": "phishing_fraud_detection",
};

export type PermissionInstanceConfig = (typeof DOMAIN_TO_INSTANCE_CONFIG)[keyof typeof DOMAIN_TO_INSTANCE_CONFIG];
export type PermissionInstanceConfigs = Partial<Pick<OrgConfigs, PermissionInstanceConfig>>;
export const INSTANCE_CONFIGS = uniq(Object.values(DOMAIN_TO_INSTANCE_CONFIG)) as PermissionInstanceConfig[];

const DOMAIN_BITMASKS = Object.fromEntries(
  Object.entries(DOMAIN_TO_INSTANCE_CONFIG).map(([domain, instanceConfig]) => [
    instanceConfig,
    Object.keys(UserPermissions)
      .filter((key) => key.startsWith(domain))
      .reduce((acc, curr) => acc | UserPermissions[curr as never], UserPermissions.none),
  ])
) as Record<PermissionInstanceConfig, UserPermissions>;

export const getInstanceConfigsFromBitmap = memoize((granularPermission: UserPermissions) => {
  const usr = { permissions: granularPermission };
  const domains = new Set(
    Object.entries(UserPermissions)
      .filter(([, value]) => hasUserPermission(usr, value))
      .map(([key]) => key.split(".")[0])
  );
  const instanceConfigs = Object.entries(DOMAIN_TO_INSTANCE_CONFIG)
    .filter(([domain]) => domains.has(domain))
    .map(([, permission]) => permission);
  return instanceConfigs;
});

export type PartialUser = Pick<BaseUser, "permissions" | PermissionInstanceConfig> & {
  permissionsInstanceConfigs?: PermissionInstanceConfigs;
};

export function getOptionalPermissions(grantingUser?: PartialUser & { isAnyAdmin?: boolean | undefined; isSuperAdmin?: boolean | undefined }) {
  return grantingUser?.isAnyAdmin || grantingUser?.isSuperAdmin
    ? INSTANCE_CONFIGS
    : getInstanceConfigsFromBitmap(BigInt(grantingUser?.permissions || 0));
}

export const hasPermission = (user: PartialUser | undefined, granularPermission: UserPermissions) => {
  const userConfigs: PermissionInstanceConfigs | undefined = user?.permissionsInstanceConfigs ?? user;
  if (!user || !userConfigs) return false;
  const instanceConfigs = getInstanceConfigsFromBitmap(granularPermission);
  if (!instanceConfigs.some((instanceConfig) => userConfigs[instanceConfig])) {
    return false;
  }
  return hasUserPermission(user, granularPermission);
};

export const hasAnyPermission = (user: PartialUser | undefined, granularPermissions: UserPermissions[]) => {
  return granularPermissions.some((granularPermission) => hasPermission(user, granularPermission));
};

export const canUserLoginToPlatform = (user: BaseUser | UserType | undefined) => {
  if (!user) return false;
  if (canUserLoginToDesktop(user)) {
    return true;
  }
  return INSTANCE_CONFIGS.some((config) => {
    const instanceConfig = user[config];
    const hasPermission = BigInt(user?.permissions || 0) & (DOMAIN_BITMASKS[config] || 0n);
    return Boolean(instanceConfig && hasPermission);
  });
};

export const getPermissionNames = (permissionBitmap: string): string[] => {
  const permissions = BigInt(permissionBitmap || "0");
  const permissionNames: string[] = [];

  for (const [permissionName, permissionValue] of Object.entries(UserPermissions)) {
    if (permissionName !== "none" && typeof permissionValue === "bigint") {
      if ((permissions & permissionValue) !== 0n) {
        permissionNames.push(permissionName);
      }
    }
  }

  return permissionNames;
};

export const getPermissionChanges = (oldPermissions: string, newPermissions: string) => {
  const oldPermissionNames = getPermissionNames(oldPermissions);
  const newPermissionNames = getPermissionNames(newPermissions);

  return {
    added: newPermissionNames.filter((permission) => !oldPermissionNames.includes(permission)),
    removed: oldPermissionNames.filter((permission) => !newPermissionNames.includes(permission)),
  };
};

export const getInstanceConfigsFromOrgConfigs = (orgConfigs: Partial<OrgConfigs>): PermissionInstanceConfigs => {
  return INSTANCE_CONFIGS.reduce((acc: Partial<OrgConfigs>, curr) => {
    acc[curr] = orgConfigs[curr] ?? (orgConfigsObject[curr].defaultValue as any);
    return acc;
  }, {});
};
